import React from 'react';
import { Helmet } from 'react-helmet-async';

export interface SEOProps {
  title: string;                    // 50-60 chars, brand at end
  description: string;              // 150-158 chars, keyword + CTA
  canonical: string;                // Full URL incl. https://www.gograbjob.com
  ogImage?: string;                 // Full URL; defaults to /og-default.png
  ogType?: 'website' | 'article' | 'product';
  noIndex?: boolean;                // true for /admin, /debug-logs
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]; // page-specific schema
  /** India geo signals — homepage, about, contact (W2.2). */
  includeGeo?: boolean;
  /** Article Open Graph — blog posts (W2.2). */
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
  /** LCP images to preload on this route only (avoids unused preload on SPA routes). */
  preloadImages?: { href: string; fetchPriority?: 'high' | 'low' | 'auto' }[];
}

const DEFAULT_OG = 'https://www.gograbjob.com/og-default.png';

/**
 * Netlify serves prerendered marketing routes from `<route>/index.html`, so the
 * canonical 200 URL has a trailing slash (`/pricing/`), and the non-slash form
 * 301-redirects to it. Canonical tags + og:url must point at the 200 URL or
 * Google reports "Page with redirect". Normalize every canonical to the
 * trailing-slash form (except the root and file-like paths such as /sitemap.xml).
 */
function normalizeCanonical(url: string): string {
  try {
    const u = new URL(url);
    const hasExtension = /\.[a-z0-9]+$/i.test(u.pathname);
    if (u.pathname !== '/' && !u.pathname.endsWith('/') && !hasExtension) {
      u.pathname += '/';
    }
    return u.toString();
  } catch {
    return url;
  }
}

/** Zykes / GoGrabJob HQ — Electronic City Phase 1, Bengaluru */
const GEO_DEFAULTS = {
  region: 'IN-KA',
  placename: 'Electronic City Phase 1, Bengaluru, India',
  position: '12.8431;77.6565',
  icbm: '12.8431, 77.6565',
} as const;

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG,
  ogType = 'website',
  noIndex = false,
  jsonLd,
  includeGeo = false,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  preloadImages,
}) => {
  const ldArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  const canonicalUrl = normalizeCanonical(canonical);
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta
        name="robots"
        content={
          noIndex
            ? 'noindex, nofollow'
            : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
        }
      />

      <meta name="application-name" content="GoGrabJob" />
      <meta name="apple-mobile-web-app-title" content="GoGrabJob" />

      {includeGeo ? (
        <meta name="geo.region" content={GEO_DEFAULTS.region} />
      ) : null}
      {includeGeo ? (
        <meta name="geo.placename" content={GEO_DEFAULTS.placename} />
      ) : null}
      {includeGeo ? (
        <meta name="geo.position" content={GEO_DEFAULTS.position} />
      ) : null}
      {includeGeo ? <meta name="ICBM" content={GEO_DEFAULTS.icbm} /> : null}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="GoGrabJob" />

      {ogType === 'article' && articlePublishedTime && (
        <meta property="article:published_time" content={articlePublishedTime} />
      )}
      {ogType === 'article' && articleModifiedTime && (
        <meta property="article:modified_time" content={articleModifiedTime} />
      )}
      {ogType === 'article' && articleAuthor && (
        <meta property="article:author" content={articleAuthor} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {ldArray.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}

      {preloadImages?.map(({ href, fetchPriority }) => (
        <link
          key={href}
          rel="preload"
          as="image"
          href={href}
          {...(fetchPriority ? { fetchPriority } : {})}
        />
      ))}
    </Helmet>
  );
};
